import { CalendarMonth, CheckCircle, Circle, Numbers, TextFields } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../contexts/UserContext';

/**
 * A pane displaying custom fields for a contact.
 *
 * @param {Object} props The component props.
 * @param {Object} props.contact The contact to display custom fields for.
 */
export function CustomFieldsPane({ contact }) {
	/** The user context state */
	const userContext = useContext(UserContext);

	/** The custom fields to display. */
	const [customFields, setCustomFields] = useState({});

	// Whenever the contact changes, update the custom fields.
	useEffect(() => {
		let contactCustomFields = {}
		// Only parse if type is string
		if (typeof contact?.customFields === 'string') {
			contactCustomFields = JSON.parse(contact.customFields);
		} else {
			contactCustomFields = contact?.customFields || {}
		}

		// Remove all of the phone custom fields from the contact's custom fields.
		const nonPhoneCustomFieldDefinitions = userContext.customFields.filter((field) => field.type !== 'Phone');
		const filteredCustomFields = {};
		Object.entries(contactCustomFields).forEach(([key, value]) => {
			if (nonPhoneCustomFieldDefinitions.some((fieldDefinition) => fieldDefinition.name === key)) {
				filteredCustomFields[key] = value;
			}
		});
		setCustomFields(filteredCustomFields);
	}, [contact]);

	/**
	 * Gets the icon component for a given custom field.
	 *
	 * @param {string} fieldName The field name.
	 * @returns {JSX.Element} The icon component.
	 */
	function getIconForField(fieldName) {
		const fieldDefinition = userContext.customFields.find((field) => field.name === fieldName);
		if (!fieldDefinition) {
			return Circle;
		}
		return getIconForFieldType(fieldDefinition.type);
	}

	/**
	 * Gets the display name for a custom field.
	 *
	 * @param {string} fieldName The field name.
	 * @returns {string} The display name.
	 */
	function getDisplayNameForField(fieldName) {
		const fieldDefinition = userContext.customFields.find((field) => field.name === fieldName);
		return fieldDefinition ? fieldDefinition.displayName : fieldName;
	}

	/**
	 * Gets the display value for a custom field. Necessary for formatting `Boolean` and `DateTime` fields.
	 *
	 * @param {string} fieldName The field name.
	 * @param {*} value The field value.
	 * @returns {string} The display value.
	 */
	function getDisplayValueForField(fieldName, value) {
		const fieldDefinition = userContext.customFields.find((field) => field.name === fieldName);
		if (value === null || value === undefined || value === '') {
			return '—';
		}
		if (!fieldDefinition) {
			return value;
		}

		switch (fieldDefinition.type) {
			case 'Boolean':
				return value ? 'True' : 'False';
			case 'DateTime':
				try {
					const date = new Date(+value);
					return date.toLocaleString(undefined, {
						year: 'numeric',
						month: '2-digit',
						day: '2-digit',
					});
				} catch (error) {
					return value;
				}
			default:
				return value;
		}
	}

	/**
	 * Gets the icon component for a given field type.
	 *
	 * @param {'String' | 'Number' | 'DateTime' | 'Boolean'} fieldType The field type.
	 * @returns {JSX.Element} The icon component.
	 */
	function getIconForFieldType(fieldType) {
		switch (fieldType) {
			case 'String':
				return TextFields;
			case 'Number':
				return Numbers;
			case 'DateTime':
				return CalendarMonth;
			case 'Boolean':
				return CheckCircle;
			default:
				return Circle;
		}
	}

	return (
		<>
			<Typography variant="h6" textAlign="center" gutterBottom>Custom Fields</Typography>
			{Object.keys(customFields).length === 0 &&
				<Typography variant="body2" textAlign="center" padding="50px">No fields to display</Typography>
			}
			<Box
				display="grid"
				gridTemplateColumns={{
					xs: "1fr 1fr",
					sm: "1fr 1fr 1fr",
					md: "1fr 1fr 1fr 1fr"
				}}
				justifyItems="center"
				gap={2}
			>
				{Object.entries(customFields).map(([field, value]) => {
					return (
						<Box key={field} display="grid" gridTemplateRows="auto 1fr" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
							<Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" gap={1}>
								{(() => {
									const Icon = getIconForField(field);
									return <Icon fontSize="small" color="primary" />;
								})()}
								<Typography variant="caption">{getDisplayNameForField(field)}</Typography>
							</Box>
							<Typography
								variant="body1"
								sx={{
									textAlign: 'center'
								}}>
								{getDisplayValueForField(field, value)}
							</Typography>
						</Box>
					);
				})}
			</Box>
		</>
	);
}
